/**
 * NumericInput (U5): controlled number entry with formatted display at rest,
 * raw text while editing, arrow-key stepping, clamp + round on commit.
 * Display-only math — business totals come from the shared engine (U3).
 */
import { useId, useRef, useState, type JSX, type KeyboardEvent, type ReactNode } from 'react';
import { cx } from './cx';
import { Field } from './Field';
import type { ControlSize } from './Button';
import { clampNumber, formatNumber, parseNumeric, roundTo } from './format';

export interface NumericInputProps {
  value: number | null;
  onValueChange: (value: number | null) => void;
  min?: number;
  max?: number;
  /** Arrow-key increment. Default 1. */
  step?: number;
  /** Display decimals. Default 2. */
  decimals?: number;
  /** Trim trailing zeros in the rest display (nice for quantities). */
  trim?: boolean;
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  size?: ControlSize;
  prefix?: ReactNode;
  suffix?: ReactNode;
  align?: 'start' | 'end';
  /** Monospace tabular numerals. Default true. */
  mono?: boolean;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  full?: boolean;
  id?: string;
  name?: string;
  'aria-label'?: string;
}

export function NumericInput({
  value,
  onValueChange,
  min,
  max,
  step = 1,
  decimals = 2,
  trim = false,
  label,
  hint,
  error,
  size = 'md',
  prefix,
  suffix,
  align = 'end',
  mono = true,
  placeholder,
  disabled,
  required,
  full,
  id,
  name,
  'aria-label': ariaLabel,
}: NumericInputProps): JSX.Element {
  const autoId = useId();
  const inputId = id ?? autoId;
  const describedById = hint != null || error ? `${inputId}-desc` : undefined;

  // While focused, the raw draft text is the source of truth; at rest we
  // render the formatted committed value.
  const [focused, setFocused] = useState(false);
  const [text, setText] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const restDisplay = value === null ? '' : formatNumber(value, { decimals, trim });

  function commit(raw: string): void {
    const parsed = parseNumeric(raw);
    if (parsed === null) {
      if (value !== null) onValueChange(null);
      return;
    }
    if (Number.isNaN(parsed)) return; // unparseable → revert to prior value
    const next = roundTo(clampNumber(parsed, min, max), decimals);
    if (next !== value) onValueChange(next);
  }

  function nudge(direction: 1 | -1): void {
    const base = focused ? (parseNumeric(text) ?? value ?? 0) : (value ?? 0);
    const start = Number.isNaN(base) ? (value ?? 0) : base;
    const next = roundTo(clampNumber(start + direction * step, min, max), decimals);
    onValueChange(next);
    setText(String(next));
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      nudge(1);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      nudge(-1);
    } else if (event.key === 'Enter') {
      commit(text);
      inputRef.current?.blur();
    } else if (event.key === 'Escape') {
      setText(value === null ? '' : String(value));
    }
  }

  return (
    <Field
      htmlFor={inputId}
      label={label}
      hint={hint}
      error={error}
      required={required}
      full={full}
      describedById={describedById}
    >
      <span
        className={cx(
          'bl-control',
          `bl-control--${size}`,
          full && 'bl-control--full',
          Boolean(error) && 'is-invalid',
          disabled && 'is-disabled',
        )}
      >
        {prefix != null && <span className="bl-control__affix">{prefix}</span>}
        <input
          ref={inputRef}
          id={inputId}
          name={name}
          className={cx(
            'bl-control__input',
            align === 'end' && 'bl-control__input--end',
            mono && 'bl-control__input--mono',
          )}
          inputMode="decimal"
          autoComplete="off"
          value={focused ? text : restDisplay}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedById}
          aria-label={ariaLabel}
          onFocus={(event) => {
            setFocused(true);
            setText(value === null ? '' : String(value));
            event.currentTarget.select();
          }}
          onBlur={() => {
            commit(text);
            setFocused(false);
          }}
          onChange={(event) => setText(event.currentTarget.value)}
          onKeyDown={handleKeyDown}
        />
        {suffix != null && <span className="bl-control__affix">{suffix}</span>}
      </span>
    </Field>
  );
}
